import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import { getCancellationStatusThunk } from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectCancellationStatus,
  selectStatusLoading,
  selectStatusError,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const CancellationStatus = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const status = useSelector(selectCancellationStatus);
  const loading = useSelector(selectStatusLoading);
  const error = useSelector(selectStatusError);

  useEffect(() => {
    dispatch(getCancellationStatusThunk(planId));
  }, [planId, dispatch]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const getSettlementStatusBadge = (settlement) => {
    if (!settlement) return null;

    const statusConfig = {
      PENDING: {
        color: "warning",
        icon: ClockIcon,
        text: "Pending",
        bg: "bg-warning-light",
        textColor: "text-warning-dark",
      },
      PROCESSING: {
        color: "secondary",
        icon: ClockIcon,
        text: "Processing",
        bg: "bg-secondary-light",
        textColor: "text-secondary-dark",
      },
      APPROVED: {
        color: "primary",
        icon: CheckCircleIcon,
        text: "Approved",
        bg: "bg-primary-light",
        textColor: "text-primary-dark",
      },
      COMPLETED: {
        color: "success",
        icon: CheckCircleIcon,
        text: "Completed",
        bg: "bg-success-light",
        textColor: "text-success-dark",
      },
      FAILED: {
        color: "danger",
        icon: XCircleIcon,
        text: "Failed",
        bg: "bg-danger-light",
        textColor: "text-danger-dark",
      },
      CANCELLED: {
        color: "neutral",
        icon: XCircleIcon,
        text: "Cancelled",
        bg: "bg-neutral-light",
        textColor: "text-neutral-dark",
      },
    };

    const currentStatus = settlement.currentStatus || settlement.status;
    const config = statusConfig[currentStatus] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.textColor}`}
      >
        <Icon className="h-4 w-4 mr-2" />
        {config.text}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      CUSTOMER_REQUEST: { label: "Customer Request", color: "primary" },
      FINANCIAL_HARDSHIP: { label: "Financial Hardship", color: "warning" },
      MEDICAL_EMERGENCY: { label: "Medical Emergency", color: "danger" },
      DEATH: { label: "Death", color: "neutral" },
      RELOCATION: { label: "Relocation", color: "secondary" },
      FRAUD_SUSPECTED: { label: "Fraud Suspected", color: "danger" },
      ADMIN_ACTION: { label: "Admin Action", color: "neutral" },
      POLICY_VIOLATION: { label: "Policy Violation", color: "danger" },
      OTHER: { label: "Other", color: "neutral" },
    };

    const config = categoryConfig[category] || categoryConfig.OTHER;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-${config.color}-light text-${config.color}-dark`}
      >
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-danger-light border border-danger rounded-lg p-6">
          <div className="flex items-start">
            <XCircleIcon className="h-6 w-6 text-danger mt-0.5 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-danger-dark">
                Error Loading Status
              </h3>
              <p className="mt-2 text-sm text-danger-dark">{error}</p>
              <button
                onClick={() => navigate("/jewellery/active")}
                className="mt-4 px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark"
              >
                Back to Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <DocumentTextIcon className="h-12 w-12 text-neutral mx-auto mb-4" />
          <p className="text-neutral-dark">Cancellation status not found</p>
          <button
            onClick={() => navigate("/jewellery/active")}
            className="mt-4 text-primary hover:text-primary-dark"
          >
            Back to Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Cancellation Status</h1>
        <div className="mt-2 flex items-center gap-3">
          <p className="text-sm text-neutral-dark">
            Plan #{status.planNumber} cancelled on{" "}
            {formatDate(status.cancellationDate)}
          </p>
          {status.category && getCategoryBadge(status.category)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cancellation Details */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">
              Cancellation Details
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-neutral-dark">Reason</p>
                <p className="font-medium">{status.reason || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Cancelled By</p>
                <p className="font-medium">
                  {status.cancelledBy?.name || "System"}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Date & Time</p>
                <p className="font-medium">
                  {formatDate(status.cancellationDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Settlement Details */}
          {status.settlement && (
            <div className="bg-background rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-text">
                  Settlement Details
                </h2>
                {getSettlementStatusBadge(status.settlement)}
              </div>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-dark">Type</span>
                  <span className="font-medium">
                    {status.settlement.type || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-dark">Amount</span>
                  <span className="font-medium text-lg">
                    {formatAmount(status.settlement.amount)}
                  </span>
                </div>
                {status.settlement.method && (
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-dark">Method</span>
                    <span className="font-medium">
                      {status.settlement.method}
                    </span>
                  </div>
                )}
                {status.settlement.reference && (
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-dark">Reference</span>
                    <span className="font-mono text-xs">
                      {status.settlement.reference}
                    </span>
                  </div>
                )}
                {status.settlement.lastUpdated && (
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-dark">
                      Last Updated
                    </span>
                    <span className="text-sm">
                      {formatDate(status.settlement.lastUpdated)}
                    </span>
                  </div>
                )}
              </div>

              {/* Settlement Type Messages */}
              {status.settlement.type === "REFUND" &&
                status.settlement.currentStatus !== "COMPLETED" && (
                  <div className="mt-4 p-4 bg-secondary-light rounded-lg">
                    <p className="text-sm text-secondary-dark flex items-center">
                      <CurrencyRupeeIcon className="h-4 w-4 inline mr-2" />
                      Refund will be processed within 7-10 business days
                    </p>
                  </div>
                )}

              {status.settlement.type === "COLLECTION" && (
                <div className="mt-4 p-4 bg-warning-light rounded-lg">
                  <p className="text-sm text-warning-dark flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 inline mr-2" />
                    Customer needs to pay{" "}
                    {formatAmount(status.settlement.amount)}
                  </p>
                </div>
              )}

              {status.settlement.type === "NONE" && (
                <div className="mt-4 p-4 bg-success-light rounded-lg">
                  <p className="text-sm text-success-dark flex items-center">
                    <CheckCircleIcon className="h-4 w-4 inline mr-2" />
                    No settlement required - perfectly balanced
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">Actions</h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/jewellery/active")}
                className="w-full px-4 py-2 bg-primary text-text-on-primary rounded-lg hover:bg-primary-dark"
              >
                View All Plans
              </button>
              <button
                onClick={() => navigate(`/jewellery/${planId}`)}
                className="w-full px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary-light"
              >
                View Plan Details
              </button>
              <button
                onClick={() => navigate(`/jewellery/cancellations/refunds`)}
                className="w-full px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary-light"
              >
                Approve Refunds
              </button>
              <button
                onClick={() => window.print()}
                className="w-full px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light"
              >
                Print Status
              </button>
            </div>
          </div>

          {/* Status Summary */}
          <div className="bg-success-light border border-success rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-success mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-success-dark">
                  Plan Successfully Cancelled
                </p>
                <p className="text-xs text-success-dark mt-1">
                  All pending installments have been cancelled
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps (if applicable) */}
          {status.settlement?.type === "REFUND" &&
            status.settlement?.currentStatus !== "COMPLETED" && (
              <div className="bg-secondary-light border border-secondary rounded-lg p-4">
                <h3 className="text-sm font-medium text-text mb-2">
                  Next Steps
                </h3>
                <ul className="text-xs text-neutral-dark space-y-1 list-disc list-inside">
                  <li>Refund request has been submitted</li>
                  <li>Wait for approval (1-2 business days)</li>
                  <li>Processing (2-3 business days)</li>
                  <li>Amount credited (5-7 business days)</li>
                </ul>
              </div>
            )}

          {status.settlement?.type === "COLLECTION" && (
            <div className="bg-warning-light border border-warning rounded-lg p-4">
              <h3 className="text-sm font-medium text-text mb-2">
                Payment Required
              </h3>
              <p className="text-xs text-warning-dark">
                Please visit the branch to complete the payment of{" "}
                {formatAmount(status.settlement.amount)} to close this
                cancellation.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancellationStatus;
